// src/components/admin/IndustryDetailsPage.tsx - Fully Editable Industry Detail Page
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Divider,
  Container,
  Breadcrumbs,
  Link,
  useTheme,
  alpha,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CardActionArea,
  ListItemIcon,
  Tooltip,
  Fab
} from '@mui/material';
import {
  NavigateNext as NavigateNextIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  ExpandMore as ExpandMoreIcon,
  Dashboard as DashboardIcon,
  Gavel as GavelIcon,
  Lightbulb as LightbulbIcon,
  TrendingUp as TrendingUpIcon,
  ArrowForward as ArrowForwardIcon,
  People as PeopleIcon,
  MonetizationOn as MonetizationOnIcon,
  Science as ScienceIcon,
  Article as ArticleIcon,
  Check as CheckIcon,
  Category as CategoryIcon,
  DragIndicator as DragIndicatorIcon,
  TextFields as TextFieldsIcon,
  Send as SendIcon,
  CheckCircle as ApproveIcon,
  HideSource as HideIcon,
  Print as PrintIcon,
  Medication as PillIcon,
  Flight as PlaneIcon,
  DirectionsCar as CarIcon,
  Storage as ServerIcon,
  Explore as TourIcon,
  Apartment as BuildingIcon,
  AttachMoney as CurrencyIcon,
  LocalGasStation as OilIcon,
  Terrain as MiningIcon,
  Restaurant as FoodIcon,
  WbSunny as SunIcon,
  Favorite as HeartIcon,
  LocalShipping as TruckIcon,
  // All icons from IconPicker
  Factory,
  Store,
  Engineering,
  Agriculture,
  AccountBalance,
  Analytics,
  Assessment,
  BarChart,
  PieChart,
  ShowChart,
  Timeline,
  Computer,
  PhoneAndroid,
  Devices,
  Memory,
  Cloud,
  CloudQueue,
  DeveloperMode,
  Code,
  Terminal,
  Biotech,
  Psychology,
  Bolt,
  ElectricBolt,
  WaterDrop,
  LocalFireDepartment,
  EnergySavingsLeaf,
  Recycling,
  Park,
  Forest,
  Nature,
  Fastfood,
  LocalDining,
  LocalBar,
  LocalCafe,
  Icecream,
  EmojiFoodBeverage,
  SetMeal,
  RiceBowl,
  LocalPizza,
  LocalHospital,
  Medication,
  Vaccines,
  HealthAndSafety,
  MedicalServices,
  Healing,
  FitnessCenter,
  SportsGymnastics,
  SelfImprovement,
  Spa,
  DirectionsBus,
  Train,
  DirectionsBoat,
  LocalAirport,
  RocketLaunch,
  TwoWheeler,
  ElectricCar,
  ElectricScooter,
  Construction,
  HomeWork,
  House,
  Cottage,
  Villa,
  OtherHouses,
  Foundation,
  Roofing,
  Plumbing,
  Handyman,
  School,
  MenuBook,
  AutoStories,
  LibraryBooks,
  LocalLibrary,
  Bookmark,
  Class,
  CastForEducation,
  Calculate,
  Functions,
  Phone,
  Email,
  Wifi,
  SignalCellularAlt,
  Satellite,
  Radio,
  Tv,
  Podcasts,
  Videocam,
  CameraAlt,
  PhotoCamera,
  AccountBalanceWallet,
  CreditCard,
  Payments,
  CurrencyExchange,
  MoneyOff,
  PriceCheck,
  Receipt,
  PointOfSale,
  ShoppingCart,
  ShoppingBag,
  Hotel,
  Luggage,
  BeachAccess,
  Landscape,
  Hiking,
  Tour,
  Castle,
  Museum,
  TheaterComedy,
  Festival,
  Sports,
  SportsBaseball,
  SportsSoccer,
  SportsFootball,
  SportsTennis,
  SportsEsports,
  VideogameAsset,
  Movie,
  Theaters,
  LiveTv,
  Storefront,
  LocalMall,
  LocalGroceryStore,
  Inventory,
  ShoppingBasket,
  AddShoppingCart,
  LocalOffer,
  Discount,
  Sell,
  CleaningServices,
  DryCleaning,
  LocalLaundryService,
  Checkroom,
  ContentCut,
  DesignServices,
  ColorLens,
  Brush,
  Build,
  Settings,
  Hardware,
  Extension,
  Groups,
  Person,
  EmojiPeople,
  Diversity1,
  Wc,
  FamilyRestroom,
  ChildCare,
  Accessibility,
  Description,
  Assignment,
  Topic,
  Folder,
  InsertDriveFile,
  PictureAsPdf,
  Source,
  DataObject,
  Security,
  Shield,
  VerifiedUser,
  Lock,
  Key,
  AdminPanelSettings,
  Policy,
  GppGood,
  LocalPolice,
  Star,
  Grade,
  Diamond,
  Verified,
  Celebration,
  EmojiEvents,
  Circle
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import * as industryApi from '../../services/industry';
import type { Industry, SubIndustry } from '../../services/industry';
import { valueChains } from '../../data/valueChainData';

// Import the editable ValueChainTable component
import EditableValueChainTable from '../valueChain/EditableValueChainTable';
import IconPicker from './IconPicker';
import TabDataEditor from './TabDataEditor';
import UserFriendlySupportEcosystemEditor from './UserFriendlySupportEcosystemEditor';
import {
  RenderMarketOverview,
  RenderLegalInfrastructure,
  RenderIncentives,
  RenderTrends,
  RenderTechnology
} from './IndustryTabRenderers';

const MotionPaper = motion(Paper);

// TypeScript interfaces (keeping existing ones and adding new ones)
interface KeyMetrics {
  employment: string;
  exports: string;
  companies: number;
}

interface GrowthDataPoint {
  year: string;
  value: number;
}

interface TabContent {
  marketOverview?: any;
  legalInfrastructure?: any;
  trends?: any;
  incentives?: any;
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

// Editable Text Component
const EditableText: React.FC<{
  value: string;
  onSave: (value: string) => void;
  variant?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'body1' | 'body2' | 'subtitle1' | 'subtitle2';
  multiline?: boolean;
  rows?: number;
  placeholder?: string;
  fontWeight?: number;
  color?: string;
  sx?: any;
}> = ({ 
  value, 
  onSave, 
  variant = 'body1', 
  multiline = false, 
  rows = 1, 
  placeholder = 'Click to edit...',
  fontWeight,
  color,
  sx = {}
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const theme = useTheme();

  const handleSave = () => {
    onSave(editValue);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(value);
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <Box sx={{ position: 'relative', ...sx }}>
        <TextField
          fullWidth
          multiline={multiline}
          rows={rows}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          placeholder={placeholder}
          variant="outlined"
          size="small"
          onBlur={handleSave}
          onKeyPress={(e) => {
            if (e.key === 'Enter' && !multiline) {
              handleSave();
            }
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: alpha(theme.palette.primary.main, 0.05),
              '& fieldset': {
                borderColor: theme.palette.primary.main,
              }
            }
          }}
          InputProps={{
            endAdornment: (
              <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
                <IconButton size="small" onClick={handleSave} color="primary">
                  <CheckIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={handleCancel}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            )
          }}
          autoFocus
        />
      </Box>
    );
  }

  return (
    <Box 
      sx={{
        position: 'relative',
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: alpha(theme.palette.primary.main, 0.05),
          '& .edit-indicator': {
            opacity: 1
          }
        },
        p: 0.5,
        borderRadius: 1,
        ...sx
      }}
      onClick={() => setIsEditing(true)}
    >
      <Typography 
        variant={variant} 
        sx={{ 
          fontWeight, 
          color, 
          minHeight: '1em',
          ...(value ? {} : { 
            color: theme.palette.text.disabled,
            fontStyle: 'italic'
          })
        }}
      >
        {value || placeholder}
      </Typography>
      <TextFieldsIcon 
        className="edit-indicator"
        sx={{
          position: 'absolute',
          top: 2,
          right: 2,
          fontSize: 12,
          opacity: 0,
          transition: 'opacity 0.2s',
          color: theme.palette.primary.main
        }}
      />
    </Box>
  );
};

// Editable List Component
const EditableList: React.FC<{
  items: string[];
  onUpdate: (items: string[]) => void;
  title?: string;
  allowAdd?: boolean;
  allowDelete?: boolean;
}> = ({ items, onUpdate, title, allowAdd = true, allowDelete = true }) => {
  const theme = useTheme();
  
  const handleItemChange = (index: number, value: string) => {
    const newItems = [...items];
    newItems[index] = value;
    onUpdate(newItems);
  };

  const handleAddItem = () => {
    onUpdate([...items, '']);
  };

  const handleDeleteItem = (index: number) => {
    const newItems = items.filter((_, i) => i !== index);
    onUpdate(newItems);
  };

  return (
    <Box sx={{ mt: 2 }}>
      {title && (
        <Typography variant="h6" fontWeight={600} color="primary.main" gutterBottom>
          {title}
        </Typography>
      )}
      <List sx={{ mt: 1 }}>
        {items.map((item, index) => (
          <ListItem key={index} sx={{ py: 1, px: 0 }}>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <CheckIcon color="primary" fontSize="small" />
            </ListItemIcon>
            <EditableText
              value={item}
              onSave={(value) => handleItemChange(index, value)}
              placeholder="Enter item..."
              sx={{ flex: 1 }}
            />
            {allowDelete && (
              <ListItemSecondaryAction>
                <IconButton
                  size="small"
                  onClick={() => handleDeleteItem(index)}
                  sx={{ color: theme.palette.error.main }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </ListItemSecondaryAction>
            )}
          </ListItem>
        ))}
      </List>
      {allowAdd && (
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAddItem}
          sx={{ mt: 1 }}
          variant="outlined"
        >
          Add Item
        </Button>
      )}
    </Box>
  );
};

// Editable Object Component for complex nested objects
const EditableObject: React.FC<{
  data: any;
  onUpdate: (data: any) => void;
  title?: string;
}> = ({ data, onUpdate, title }) => {
  const theme = useTheme();

  const handleFieldChange = (field: string, value: any) => {
    onUpdate({
      ...data,
      [field]: value
    });
  };

  const handleNestedFieldChange = (parentKey: string, childKey: string, value: any) => {
    const updatedParent = {
      ...data[parentKey],
      [childKey]: value
    };
    handleFieldChange(parentKey, updatedParent);
  };

  const renderComplexField = (key: string, value: any, depth = 0, parentPath = '') => {
    const currentPath = parentPath ? `${parentPath}.${key}` : key;
    const indent = depth * 20;

    // Handle arrays of strings
    if (Array.isArray(value) && value.every(item => typeof item === 'string')) {
      return (
        <Box key={currentPath} sx={{ ml: `${indent}px`, mb: 2 }}>
          <EditableList
            items={value}
            onUpdate={(newItems) => {
              if (parentPath) {
                const pathParts = parentPath.split('.');
                if (pathParts.length === 1) {
                  handleNestedFieldChange(pathParts[0], key, newItems);
                } else {
                  // Handle deeper nesting if needed
                  handleFieldChange(pathParts[0], {
                    ...data[pathParts[0]],
                    [key]: newItems
                  });
                }
              } else {
                handleFieldChange(key, newItems);
              }
            }}
            title={key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
          />
        </Box>
      );
    }

    // Handle arrays of objects (like incentives sections)
    if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') {
      return (
        <Box key={currentPath} sx={{ ml: `${indent}px`, mb: 3 }}>
          <Typography variant="h6" fontWeight={600} color="primary.main" gutterBottom>
            {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
          </Typography>
          {value.map((item, index) => (
            <Paper key={index} elevation={1} sx={{ p: 2, mb: 2, backgroundColor: alpha(theme.palette.background.default, 0.3) }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Item {index + 1}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => {
                    const newArray = value.filter((_, i) => i !== index);
                    if (parentPath) {
                      handleNestedFieldChange(parentPath, key, newArray);
                    } else {
                      handleFieldChange(key, newArray);
                    }
                  }}
                  sx={{ color: theme.palette.error.main }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
              {Object.entries(item).map(([itemKey, itemValue]) => 
                renderComplexField(itemKey, itemValue, depth + 1, currentPath + `[${index}]`)
              )}
            </Paper>
          ))}
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => {
              const newItem = value.length > 0 ? 
                Object.keys(value[0]).reduce((acc, k) => {
                  acc[k] = Array.isArray(value[0][k]) ? [] : '';
                  return acc;
                }, {} as any) : 
                { title: '', points: [] };
              
              const newArray = [...value, newItem];
              if (parentPath) {
                handleNestedFieldChange(parentPath, key, newArray);
              } else {
                handleFieldChange(key, newArray);
              }
            }}
            variant="outlined"
            sx={{ mt: 1 }}
          >
            Add {key.slice(0, -1)} Item
          </Button>
        </Box>
      );
    }

    // Handle simple objects
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      return (
        <Box key={currentPath} sx={{ ml: `${indent}px`, mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} color="primary.main" gutterBottom>
            {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
          </Typography>
          <Paper elevation={1} sx={{ p: 2, backgroundColor: alpha(theme.palette.background.default, 0.5) }}>
            {Object.entries(value).map(([subKey, subValue]) => 
              renderComplexField(subKey, subValue, depth + 1, currentPath)
            )}
          </Paper>
        </Box>
      );
    }

    // Handle strings
    if (typeof value === 'string') {
      return (
        <Box key={currentPath} sx={{ ml: `${indent}px`, mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
          </Typography>
          <EditableText
            value={value}
            onSave={(newValue) => {
              if (parentPath) {
                const pathParts = parentPath.split('.');
                if (pathParts.length === 1) {
                  handleNestedFieldChange(pathParts[0], key, newValue);
                } else {
                  // Handle deeper nesting
                  const parentObj = { ...data[pathParts[0]] };
                  let current = parentObj;
                  for (let i = 1; i < pathParts.length - 1; i++) {
                    current = current[pathParts[i]];
                  }
                  current[key] = newValue;
                  handleFieldChange(pathParts[0], parentObj);
                }
              } else {
                handleFieldChange(key, newValue);
              }
            }}
            multiline={value.length > 100}
            rows={value.length > 100 ? 3 : 1}
            placeholder={`Enter ${key.replace(/([A-Z])/g, ' $1').toLowerCase()}...`}
          />
        </Box>
      );
    }

    // Handle numbers
    if (typeof value === 'number') {
      return (
        <Box key={currentPath} sx={{ ml: `${indent}px`, mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
          </Typography>
          <TextField
            type="number"
            value={value}
            onChange={(e) => {
              const newValue = parseFloat(e.target.value) || 0;
              if (parentPath) {
                const pathParts = parentPath.split('.');
                if (pathParts.length === 1) {
                  handleNestedFieldChange(pathParts[0], key, newValue);
                } else {
                  handleFieldChange(key, newValue);
                }
              } else {
                handleFieldChange(key, newValue);
              }
            }}
            size="small"
            fullWidth
          />
        </Box>
      );
    }

    // Handle booleans
    if (typeof value === 'boolean') {
      return (
        <Box key={currentPath} sx={{ ml: `${indent}px`, mb: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={value}
                onChange={(e) => {
                  const newValue = e.target.checked;
                  if (parentPath) {
                    const pathParts = parentPath.split('.');
                    if (pathParts.length === 1) {
                      handleNestedFieldChange(pathParts[0], key, newValue);
                    } else {
                      handleFieldChange(key, newValue);
                    }
                  } else {
                    handleFieldChange(key, newValue);
                  }
                }}
              />
            }
            label={key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
          />
        </Box>
      );
    }

    return null;
  };

  if (!data || Object.keys(data).length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No data available to edit.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {title && (
        <Typography variant="h6" fontWeight={600} color="primary.main" gutterBottom>
          {title}
        </Typography>
      )}
      {Object.entries(data).map(([key, value]) => renderComplexField(key, value))}
    </Box>
  );
};

const IndustryDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const [industry, setIndustry] = useState<Industry | null>(null);
  const [selectedSubIndustry, setSelectedSubIndustry] = useState<SubIndustry | null>(null);
  const [isNewIndustry, setIsNewIndustry] = useState(false);
  const [viewMode, setViewMode] = useState<'industry' | 'subindustry'>('industry');
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success'
  });
  const [subIndustryDialog, setSubIndustryDialog] = useState(false);
  const [editingSubIndustry, setEditingSubIndustry] = useState<SubIndustry | null>(null);
  const [iconPickerOpen, setIconPickerOpen] = useState(false);

  const categories = ['Localization', 'Service & Infrastructure', 'Mature Industries'];

  // Helper function to get industry icon component
  const getIndustryIcon = (iconName: string | null | undefined, fontSize: number = 24) => {
    const iconProps = { sx: { fontSize } };

    if (!iconName) return <BusinessIcon {...iconProps} />;

    const iconMap: Record<string, React.ReactElement> = {
      // Business & Industry
      'Business': <BusinessIcon {...iconProps} />,
      'Factory': <Factory {...iconProps} />,
      'Store': <Store {...iconProps} />,
      'LocalShipping': <TruckIcon {...iconProps} />,
      'Engineering': <Engineering {...iconProps} />,
      'Agriculture': <Agriculture {...iconProps} />,
      'Apartment': <BuildingIcon {...iconProps} />,
      'AccountBalance': <AccountBalance {...iconProps} />,
      'AttachMoney': <CurrencyIcon {...iconProps} />,
      'TrendingUp': <TrendingUpIcon {...iconProps} />,
      'Analytics': <Analytics {...iconProps} />,
      'Assessment': <Assessment {...iconProps} />,
      'BarChart': <BarChart {...iconProps} />,
      'PieChart': <PieChart {...iconProps} />,
      'ShowChart': <ShowChart {...iconProps} />,
      'Timeline': <Timeline {...iconProps} />,
      // Technology & Innovation
      'Computer': <Computer {...iconProps} />,
      'PhoneAndroid': <PhoneAndroid {...iconProps} />,
      'Devices': <Devices {...iconProps} />,
      'Memory': <Memory {...iconProps} />,
      'Storage': <ServerIcon {...iconProps} />,
      'Cloud': <Cloud {...iconProps} />,
      'CloudQueue': <CloudQueue {...iconProps} />,
      'DeveloperMode': <DeveloperMode {...iconProps} />,
      'Code': <Code {...iconProps} />,
      'Terminal': <Terminal {...iconProps} />,
      'Biotech': <Biotech {...iconProps} />,
      'Science': <ScienceIcon {...iconProps} />,
      'Psychology': <Psychology {...iconProps} />,
      'Lightbulb': <LightbulbIcon {...iconProps} />,
      // Energy & Environment
      'Bolt': <Bolt {...iconProps} />,
      'ElectricBolt': <ElectricBolt {...iconProps} />,
      'WaterDrop': <WaterDrop {...iconProps} />,
      'LocalFireDepartment': <LocalFireDepartment {...iconProps} />,
      'EnergySavingsLeaf': <EnergySavingsLeaf {...iconProps} />,
      'WbSunny': <SunIcon {...iconProps} />,
      'Recycling': <Recycling {...iconProps} />,
      'Park': <Park {...iconProps} />,
      'Forest': <Forest {...iconProps} />,
      'Nature': <Nature {...iconProps} />,
      // Food & Beverage
      'Restaurant': <FoodIcon {...iconProps} />,
      'Fastfood': <Fastfood {...iconProps} />,
      'LocalDining': <LocalDining {...iconProps} />,
      'LocalBar': <LocalBar {...iconProps} />,
      'LocalCafe': <LocalCafe {...iconProps} />,
      'Icecream': <Icecream {...iconProps} />,
      'EmojiFoodBeverage': <EmojiFoodBeverage {...iconProps} />,
      'SetMeal': <SetMeal {...iconProps} />,
      'RiceBowl': <RiceBowl {...iconProps} />,
      'LocalPizza': <LocalPizza {...iconProps} />,
      // Health & Medical
      'LocalHospital': <LocalHospital {...iconProps} />,
      'Medication': <PillIcon {...iconProps} />,
      'Vaccines': <Vaccines {...iconProps} />,
      'HealthAndSafety': <HealthAndSafety {...iconProps} />,
      'MedicalServices': <MedicalServices {...iconProps} />,
      'Healing': <Healing {...iconProps} />,
      'FitnessCenter': <FitnessCenter {...iconProps} />,
      'SportsGymnastics': <SportsGymnastics {...iconProps} />,
      'SelfImprovement': <SelfImprovement {...iconProps} />,
      'Spa': <Spa {...iconProps} />,
      // Transportation & Logistics
      'DirectionsCar': <CarIcon {...iconProps} />,
      'DirectionsBus': <DirectionsBus {...iconProps} />,
      'Train': <Train {...iconProps} />,
      'DirectionsBoat': <DirectionsBoat {...iconProps} />,
      'Flight': <PlaneIcon {...iconProps} />,
      'LocalAirport': <LocalAirport {...iconProps} />,
      'RocketLaunch': <RocketLaunch {...iconProps} />,
      'TwoWheeler': <TwoWheeler {...iconProps} />,
      'ElectricCar': <ElectricCar {...iconProps} />,
      'ElectricScooter': <ElectricScooter {...iconProps} />,
      // Construction & Real Estate
      'Construction': <Construction {...iconProps} />,
      'HomeWork': <HomeWork {...iconProps} />,
      'House': <House {...iconProps} />,
      'Cottage': <Cottage {...iconProps} />,
      'Villa': <Villa {...iconProps} />,
      'OtherHouses': <OtherHouses {...iconProps} />,
      'Foundation': <Foundation {...iconProps} />,
      'Roofing': <Roofing {...iconProps} />,
      'Plumbing': <Plumbing {...iconProps} />,
      'Handyman': <Handyman {...iconProps} />,
      // Education & Training
      'School': <School {...iconProps} />,
      'MenuBook': <MenuBook {...iconProps} />,
      'AutoStories': <AutoStories {...iconProps} />,
      'LibraryBooks': <LibraryBooks {...iconProps} />,
      'LocalLibrary': <LocalLibrary {...iconProps} />,
      'Bookmark': <Bookmark {...iconProps} />,
      'Class': <Class {...iconProps} />,
      'CastForEducation': <CastForEducation {...iconProps} />,
      'Calculate': <Calculate {...iconProps} />,
      'Functions': <Functions {...iconProps} />,
      // Communication & Media
      'Phone': <Phone {...iconProps} />,
      'Email': <Email {...iconProps} />,
      'Wifi': <Wifi {...iconProps} />,
      'SignalCellularAlt': <SignalCellularAlt {...iconProps} />,
      'Satellite': <Satellite {...iconProps} />,
      'Radio': <Radio {...iconProps} />,
      'Tv': <Tv {...iconProps} />,
      'Podcasts': <Podcasts {...iconProps} />,
      'Videocam': <Videocam {...iconProps} />,
      'CameraAlt': <CameraAlt {...iconProps} />,
      'PhotoCamera': <PhotoCamera {...iconProps} />,
      // Finance & Banking
      'AccountBalanceWallet': <AccountBalanceWallet {...iconProps} />,
      'CreditCard': <CreditCard {...iconProps} />,
      'Payments': <Payments {...iconProps} />,
      'CurrencyExchange': <CurrencyExchange {...iconProps} />,
      'MoneyOff': <MoneyOff {...iconProps} />,
      'PriceCheck': <PriceCheck {...iconProps} />,
      'Receipt': <Receipt {...iconProps} />,
      'PointOfSale': <PointOfSale {...iconProps} />,
      'ShoppingCart': <ShoppingCart {...iconProps} />,
      'ShoppingBag': <ShoppingBag {...iconProps} />,
      // Travel & Tourism
      'Hotel': <Hotel {...iconProps} />,
      'Luggage': <Luggage {...iconProps} />,
      'BeachAccess': <BeachAccess {...iconProps} />,
      'Landscape': <Landscape {...iconProps} />,
      'Hiking': <Hiking {...iconProps} />,
      'Tour': <TourIcon {...iconProps} />,
      'Castle': <Castle {...iconProps} />,
      'Museum': <Museum {...iconProps} />,
      'TheaterComedy': <TheaterComedy {...iconProps} />,
      'Festival': <Festival {...iconProps} />,
      // Entertainment & Sports
      'Sports': <Sports {...iconProps} />,
      'SportsBaseball': <SportsBaseball {...iconProps} />,
      'SportsSoccer': <SportsSoccer {...iconProps} />,
      'SportsFootball': <SportsFootball {...iconProps} />,
      'SportsTennis': <SportsTennis {...iconProps} />,
      'SportsEsports': <SportsEsports {...iconProps} />,
      'VideogameAsset': <VideogameAsset {...iconProps} />,
      'Movie': <Movie {...iconProps} />,
      'Theaters': <Theaters {...iconProps} />,
      'LiveTv': <LiveTv {...iconProps} />,
      // Retail & Commerce
      'Storefront': <Storefront {...iconProps} />,
      'LocalMall': <LocalMall {...iconProps} />,
      'LocalGroceryStore': <LocalGroceryStore {...iconProps} />,
      'Inventory': <Inventory {...iconProps} />,
      'ShoppingBasket': <ShoppingBasket {...iconProps} />,
      'AddShoppingCart': <AddShoppingCart {...iconProps} />,
      'LocalOffer': <LocalOffer {...iconProps} />,
      'Discount': <Discount {...iconProps} />,
      'Sell': <Sell {...iconProps} />,
      // Services
      'CleaningServices': <CleaningServices {...iconProps} />,
      'DryCleaning': <DryCleaning {...iconProps} />,
      'LocalLaundryService': <LocalLaundryService {...iconProps} />,
      'Checkroom': <Checkroom {...iconProps} />,
      'ContentCut': <ContentCut {...iconProps} />,
      'DesignServices': <DesignServices {...iconProps} />,
      'ColorLens': <ColorLens {...iconProps} />,
      'Brush': <Brush {...iconProps} />,
      // Tools & Equipment
      'Build': <Build {...iconProps} />,
      'Settings': <Settings {...iconProps} />,
      'Hardware': <Hardware {...iconProps} />,
      'Category': <CategoryIcon {...iconProps} />,
      'Extension': <Extension {...iconProps} />,
      'Dashboard': <DashboardIcon {...iconProps} />,
      // People & Community
      'People': <PeopleIcon {...iconProps} />,
      'Groups': <Groups {...iconProps} />,
      'Person': <Person {...iconProps} />,
      'EmojiPeople': <EmojiPeople {...iconProps} />,
      'Diversity1': <Diversity1 {...iconProps} />,
      'Wc': <Wc {...iconProps} />,
      'FamilyRestroom': <FamilyRestroom {...iconProps} />,
      'ChildCare': <ChildCare {...iconProps} />,
      'Accessibility': <Accessibility {...iconProps} />,
      // Documents & Data
      'Description': <Description {...iconProps} />,
      'Article': <ArticleIcon {...iconProps} />,
      'Assignment': <Assignment {...iconProps} />,
      'Topic': <Topic {...iconProps} />,
      'Folder': <Folder {...iconProps} />,
      'InsertDriveFile': <InsertDriveFile {...iconProps} />,
      'PictureAsPdf': <PictureAsPdf {...iconProps} />,
      'Source': <Source {...iconProps} />,
      'DataObject': <DataObject {...iconProps} />,
      // Security & Safety
      'Security': <Security {...iconProps} />,
      'Shield': <Shield {...iconProps} />,
      'VerifiedUser': <VerifiedUser {...iconProps} />,
      'Lock': <Lock {...iconProps} />,
      'Key': <Key {...iconProps} />,
      'AdminPanelSettings': <AdminPanelSettings {...iconProps} />,
      'Policy': <Policy {...iconProps} />,
      'GppGood': <GppGood {...iconProps} />,
      'LocalPolice': <LocalPolice {...iconProps} />,
      // Other
      'Star': <Star {...iconProps} />,
      'Grade': <Grade {...iconProps} />,
      'Diamond': <Diamond {...iconProps} />,
      'Verified': <Verified {...iconProps} />,
      'Celebration': <Celebration {...iconProps} />,
      'EmojiEvents': <EmojiEvents {...iconProps} />,
      'Circle': <Circle {...iconProps} />,
      // Legacy icon names (for backward compatibility)
      'PillIcon': <PillIcon {...iconProps} />,
      'PlaneIcon': <PlaneIcon {...iconProps} />,
      'CarIcon': <CarIcon {...iconProps} />,
      'ServerIcon': <ServerIcon {...iconProps} />,
      'TourIcon': <TourIcon {...iconProps} />,
      'BuildingIcon': <BuildingIcon {...iconProps} />,
      'CurrencyIcon': <CurrencyIcon {...iconProps} />,
      'OilIcon': <OilIcon {...iconProps} />,
      'MiningIcon': <MiningIcon {...iconProps} />,
      'FoodIcon': <FoodIcon {...iconProps} />,
      'SunIcon': <SunIcon {...iconProps} />,
      'HeartIcon': <HeartIcon {...iconProps} />,
      'TruckIcon': <TruckIcon {...iconProps} />
    };
    return iconMap[iconName] || <BusinessIcon {...iconProps} />;
  };

  // Get user info from localStorage
  const getUserInfo = () => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        return JSON.parse(userStr);
      }
    } catch (error) {
      console.error('Error parsing user data:', error);
    }
    return null;
  };

  const user = getUserInfo();
  const isSuperAdmin = user?.isSuperAdmin === true;

  // Get status badge properties
  const getStatusBadge = (status?: string) => {
    switch (status) {
      case 'draft':
        return { label: 'Draft', bgcolor: '#e0e0e0', textColor: '#424242' };
      case 'pending_review':
        return { label: 'Pending Review', bgcolor: alpha(theme.palette.warning.main, 0.15), textColor: theme.palette.warning.dark };
      case 'published':
        return { label: 'Published', bgcolor: alpha(theme.palette.success.main, 0.15), textColor: theme.palette.success.dark };
      case 'hidden':
        return { label: 'Hidden', bgcolor: alpha(theme.palette.error.main, 0.15), textColor: theme.palette.error.dark };
      default:
        return { label: 'Published', bgcolor: alpha(theme.palette.success.main, 0.15), textColor: theme.palette.success.dark };
    }
  };

  // Status action handlers
  const handleSubmitForReview = async () => {
    if (!industry?.id) return;
    try {
      await industryApi.submitIndustryForReview(industry.id);
      const updated = await industryApi.getIndustryById(industry.id, {
        includeSubIndustries: true,
        includeValueChain: true,
        includeSupportEcosystem: true
      });
      setIndustry(updated);
      setSnackbar({ open: true, message: 'Industry submitted for review', severity: 'success' });
    } catch (error) {
      console.error('Error submitting industry:', error);
      setSnackbar({ open: true, message: 'Failed to submit industry', severity: 'error' });
    }
  };

  const handleApprove = async () => {
    if (!industry?.id) return;
    try {
      await industryApi.approveIndustry(industry.id);
      const updated = await industryApi.getIndustryById(industry.id, {
        includeSubIndustries: true,
        includeValueChain: true,
        includeSupportEcosystem: true
      });
      setIndustry(updated);
      setSnackbar({ open: true, message: 'Industry approved and published', severity: 'success' });
    } catch (error) {
      console.error('Error approving industry:', error);
      setSnackbar({ open: true, message: 'Failed to approve industry', severity: 'error' });
    }
  };

  const handleHideIndustry = async () => {
    if (!industry?.id) return;
    if (!window.confirm('Are you sure you want to hide this industry?')) return;

    try {
      await industryApi.hideIndustry(industry.id);
      const updated = await industryApi.getIndustryById(industry.id, {
        includeSubIndustries: true,
        includeValueChain: true,
        includeSupportEcosystem: true
      });
      setIndustry(updated);
      setSnackbar({ open: true, message: 'Industry hidden', severity: 'success' });
    } catch (error) {
      console.error('Error hiding industry:', error);
      setSnackbar({ open: true, message: 'Failed to hide industry', severity: 'error' });
    }
  };

  useEffect(() => {
    const fetchIndustry = async () => {
      try {
        setLoading(true);
        if (id === 'new') {
          setIsNewIndustry(true);
          setIndustry({
            id: '',
            name: '',
            category: '',
            description: '',
            summary: '',
            hasSubIndustries: false,
            subIndustries: [],
            growthRate: 0,
            marketSize: '',
            investmentOpportunities: 0,
            keyMetrics: {},
            growthData: [],
            isActive: true,
            icon: ''
          } as any);
        } else if (id) {
          const foundIndustry = await industryApi.getIndustryById(id, {
            includeSubIndustries: true,
            includeValueChain: true,
            includeSupportEcosystem: true
          });
          if (foundIndustry) {
            setIndustry(foundIndustry);
          } else {
            navigate('/admin/industries');
          }
        } else {
          navigate('/admin/industries');
        }
      } catch (error) {
        console.error('Error fetching industry:', error);
        setSnackbar({
          open: true,
          message: 'Failed to load industry',
          severity: 'error'
        });
        navigate('/admin/industries');
      } finally {
        setLoading(false);
      }
    };

    fetchIndustry();
  }, [id, navigate]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleSave = async () => {
    if (!industry) return;

    if (!industry.name || !industry.category) {
      setSnackbar({
        open: true,
        message: 'Name and category are required fields',
        severity: 'error'
      });
      return;
    }

    try {
      console.log('=== SAVE DEBUG ===');
      console.log('currentData:', currentData);
      console.log('currentData.valueChain:', currentData?.valueChain);
      console.log('currentData.valueChain.stages FULL:', JSON.stringify(currentData?.valueChain?.stages, null, 2));

      // First, process value chain data to create new sub-industries
      if (currentData?.valueChain && currentData.valueChain.stages) {
        console.log('Processing value chain stages:', currentData.valueChain.stages);
        const existingSubIndustries = industry.subIndustries || [];
        const existingNames = new Set(existingSubIndustries.map(sub => sub.name.toLowerCase()));

        for (const stage of currentData.valueChain.stages) {
          console.log('Processing stage:', stage.name, 'content:', stage.content, 'FULL STAGE:', JSON.stringify(stage, null, 2));

          const itemsToCreate = new Set<string>();

          // Check content field
          if (stage.content) {
            const items = stage.content.split(',').map((item: string) => item.trim()).filter((item: string) => item.length > 0);
            items.forEach((item: string) => itemsToCreate.add(item));
          }

          // Check categories - look for items in all category rows
          if (stage.categories) {
            Object.entries(stage.categories).forEach(([categoryName, categoryValue]) => {
              if (categoryValue && typeof categoryValue === 'string') {
                const items = categoryValue.split(',').map((item: string) => item.trim()).filter((item: string) => item.length > 0);
                items.forEach((item: string) => itemsToCreate.add(item));
              }
            });
          }

          console.log('Items to create from stage', stage.name, ':', Array.from(itemsToCreate));

          for (const itemName of Array.from(itemsToCreate)) {
            // Check if this sub-industry already exists
            if (!existingNames.has(itemName.toLowerCase())) {
              console.log('Creating new sub-industry:', itemName);
              // Create new top-level sub-industry (parentSubIndustryId: null)
              const newSubIndustryData = {
                name: itemName,
                description: `Auto-created from value chain`,
                summary: '',
                isActive: true,
                parentSubIndustryId: null,
                displayOrder: existingSubIndustries.length
              };

              await industryApi.createSubIndustry(industry.id, newSubIndustryData);
              console.log('Successfully created:', itemName);
              existingNames.add(itemName.toLowerCase());
            } else {
              console.log('Sub-industry already exists:', itemName);
            }
          }
        }
      } else {
        console.log('No value chain data to process');
      }

      // Then save the industry
      if (isNewIndustry) {
        // Remove id field for new industries - backend will auto-generate UUID
        const { id, ...industryData } = industry;
        await industryApi.createIndustry(industryData as any);
        setSnackbar({
          open: true,
          message: 'Industry created successfully',
          severity: 'success'
        });
        navigate('/admin/industries');
      } else {
        await industryApi.updateIndustry(industry.id, industry as any);

        // Reload industry data to get new sub-industries
        const updatedIndustry = await industryApi.getIndustryById(industry.id);
        setIndustry(updatedIndustry);

        setSnackbar({
          open: true,
          message: 'Industry updated successfully. New sub-industries created from value chain.',
          severity: 'success'
        });
      }
    } catch (error: any) {
      console.error('Error saving industry:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error message:', error.message);
      setSnackbar({
        open: true,
        message: `Failed to save industry: ${error.response?.data?.error?.message || error.message || 'Unknown error'}`,
        severity: 'error'
      });
    }
  };

  const handleInputChange = (field: keyof Industry, value: any) => {
    if (!industry) return;
    setIndustry(prev => prev ? { ...prev, [field]: value } : null);
  };

  const handleKeyMetricsChange = (field: keyof KeyMetrics, value: string | number) => {
    if (!industry) return;
    setIndustry(prev => prev ? {
      ...prev,
      keyMetrics: { ...prev.keyMetrics, [field]: value }
    } : null);
  };

  const handleTagsChange = (value: string) => {
    const tags = value.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0);
    handleInputChange('tags', tags);
  };

  const handleSubIndustryClick = (subIndustry: SubIndustry) => {
    if (industry?.id) {
      navigate(`/admin/industries/${industry.id}/subindustry/${subIndustry.id}`);
    }
  };

  const handleBackToIndustry = () => {
    setSelectedSubIndustry(null);
    setViewMode('industry');
    setTabValue(0);
  };

  const handleAddSubIndustry = () => {
    setEditingSubIndustry({
      id: '',
      industryId: industry?.id || '',
      name: '',
      description: '',
      summary: '',
      isActive: true,
      growthRate: 0,
      marketSize: '',
      investmentOpportunities: 0,
      keyMetrics: { employment: '', exports: '', companies: 0 },
      growthData: []
    });
    setSubIndustryDialog(true);
  };

  const handleEditSubIndustry = (subIndustry: SubIndustry) => {
    setEditingSubIndustry(subIndustry);
    setSubIndustryDialog(true);
  };

  const handleSaveSubIndustry = () => {
    if (!editingSubIndustry || !industry) return;

    if (!editingSubIndustry.name || !editingSubIndustry.description) {
      setSnackbar({
        open: true,
        message: 'Name and description are required for sub-industries',
        severity: 'error'
      });
      return;
    }

    const updatedSubIndustries = industry.subIndustries || [];
    const existingIndex = updatedSubIndustries.findIndex(sub => sub.id === editingSubIndustry.id);

    if (existingIndex >= 0) {
      updatedSubIndustries[existingIndex] = editingSubIndustry;
    } else {
      const newId = editingSubIndustry.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
      updatedSubIndustries.push({ ...editingSubIndustry, id: newId });
    }

    setIndustry(prev => prev ? {
      ...prev,
      subIndustries: updatedSubIndustries,
      hasSubIndustries: updatedSubIndustries.length > 0
    } : null);

    setSubIndustryDialog(false);
    setEditingSubIndustry(null);
    setSnackbar({
      open: true,
      message: 'Sub-industry saved successfully',
      severity: 'success'
    });
  };

  const handleDeleteSubIndustry = (subIndustryId: string) => {
    if (!industry) return;

    const updatedSubIndustries = (industry.subIndustries || []).filter(sub => sub.id !== subIndustryId);
    setIndustry(prev => prev ? {
      ...prev,
      subIndustries: updatedSubIndustries,
      hasSubIndustries: updatedSubIndustries.length > 0
    } : null);

    setSnackbar({
      open: true,
      message: 'Sub-industry removed successfully',
      severity: 'warning'
    });
  };

  const getTabIcon = (index: number) => {
    switch (index) {
      case 0: return <DashboardIcon />;
      case 1: return <GavelIcon />;
      case 2: return <LightbulbIcon />;
      case 3: return <TrendingUpIcon />;
      case 4: return <ScienceIcon />;
      case 5: return <CategoryIcon />;
      case 6: return <BusinessIcon />;
      default: return undefined;
    }
  };

  // Get current data for display (either industry or selected sub-industry)
  const getCurrentData = () => {
    return viewMode === 'subindustry' && selectedSubIndustry ? selectedSubIndustry : industry;
  };

  const currentData = getCurrentData();

  const updateCurrentData = useCallback((field: string, value: any) => {
    if (viewMode === 'subindustry' && selectedSubIndustry) {
      const updatedSubIndustry = { ...selectedSubIndustry, [field]: value };
      setSelectedSubIndustry(updatedSubIndustry);

      // Also update in the main industry data
      setIndustry(prev => {
        if (!prev) return null;
        const updatedSubIndustries = prev.subIndustries?.map(sub =>
          sub.id === selectedSubIndustry.id ? updatedSubIndustry : sub
        ) || [];
        return { ...prev, subIndustries: updatedSubIndustries };
      });
    } else {
      setIndustry(prev => prev ? { ...prev, [field]: value } : null);
    }
  }, [viewMode, selectedSubIndustry]);

  if (loading || !industry) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
        <CircularProgress size={60} sx={{ mb: 2 }} />
        <Typography variant="h6" color="text.secondary">Loading industry data...</Typography>
      </Box>
    );
  }

  return (
    <>
      {/* Print Styles */}
      <style>{`
        @media print {
          @page {
            margin: 1.5cm;
          }

          body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

          /* Hide navigation and action buttons */
          nav, .MuiBreadcrumbs-root, .no-print {
            display: none !important;
          }

          /* Hide all buttons except content */
          button, .MuiFab-root {
            display: none !important;
          }

          /* Hide edit mode elements */
          .MuiIconButton-root, .editable-hover {
            display: none !important;
          }

          /* Ensure content is visible */
          .MuiCard-root, .MuiPaper-root {
            page-break-inside: avoid;
            break-inside: avoid;
            box-shadow: none !important;
            border: 1px solid #e0e0e0 !important;
          }

          /* Full width content */
          .MuiContainer-root {
            max-width: 100% !important;
            padding: 0 !important;
          }

          /* Optimize colors for print */
          * {
            color: #000 !important;
          }

          h1, h2, h3, h4, h5, h6 {
            color: #2563eb !important;
          }

          /* Print header */
          .print-header {
            display: block !important;
            text-align: center;
            margin-bottom: 30px;
            padding: 20px;
            background: linear-gradient(135deg, #2563eb 0%, #9333ea 100%) !important;
            color: white !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

          .print-header * {
            color: white !important;
          }

          .print-footer {
            display: block !important;
            text-align: center;
            margin-top: 30px;
            padding: 15px;
            border-top: 2px solid #2563eb;
            font-size: 12px;
          }
        }

        .print-header, .print-footer {
          display: none;
        }
      `}</style>

      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" color="primary" />}
        sx={{ mb: 4 }}
      >
        <Link
          color="inherit"
          onClick={() => navigate('/admin')}
          sx={{ cursor: 'pointer', textDecoration: 'none', '&:hover': { color: theme.palette.primary.main } }}
        >
          Admin
        </Link>
        <Link
          color="inherit"
          onClick={() => navigate('/admin/industries')}
          sx={{ cursor: 'pointer', textDecoration: 'none', '&:hover': { color: theme.palette.primary.main } }}
        >
          Industries
        </Link>
        {viewMode === 'subindustry' && selectedSubIndustry && (
          <Link
            color="inherit"
            onClick={handleBackToIndustry}
            sx={{ cursor: 'pointer', textDecoration: 'none', '&:hover': { color: theme.palette.primary.main } }}
          >
            {industry.name}
          </Link>
        )}
        <Typography color="text.primary" fontWeight="medium">
          {viewMode === 'subindustry' && selectedSubIndustry 
            ? selectedSubIndustry.name 
            : (isNewIndustry ? 'New Industry' : industry.name)
          }
        </Typography>
      </Breadcrumbs>

      {/* Hero Section */}
      <MotionPaper
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        elevation={0}
        sx={{
          mb: 4,
          p: { xs: 3, md: 4 },
          borderRadius: 3,
          background: viewMode === 'subindustry' 
            ? `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.9)}, ${alpha(theme.palette.primary.main, 0.8)})`
            : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.9)}, ${alpha(theme.palette.secondary.main, 0.8)})`,
          position: 'relative',
          overflow: 'hidden',
          color: 'white'
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box>
              <Typography variant="h3" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                {getIndustryIcon(viewMode === 'industry' ? (currentData as Industry)?.icon : industry?.icon, 48)}
                <EditableText
                  value={currentData?.name || ''}
                  onSave={(value) => updateCurrentData('name', value)}
                  variant="h3"
                  fontWeight={700}
                  color="white"
                  placeholder="Enter industry name..."
                />
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Typography variant="h6" sx={{ opacity: 0.9 }}>
                  {viewMode === 'subindustry' && selectedSubIndustry
                    ? `Sub-industry of ${industry.name}`
                    : (isNewIndustry ? 'Configure a new industry profile' : 'Manage industry details and sub-industries')
                  }
                </Typography>
                {!isNewIndustry && viewMode === 'industry' && industry && (
                  <Chip
                    label={getStatusBadge(industry.status).label}
                    sx={{
                      bgcolor: getStatusBadge(industry.status).bgcolor,
                      color: getStatusBadge(industry.status).textColor,
                      fontWeight: 600,
                      fontSize: '0.875rem'
                    }}
                  />
                )}
              </Box>
              <EditableText
                value={currentData?.description || ''}
                onSave={(value) => updateCurrentData('description', value)}
                variant="body1"
                color="white"
                multiline
                rows={2}
                placeholder="Enter industry description..."
                sx={{ opacity: 0.8, maxWidth: 600 }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {viewMode === 'subindustry' && (
                <Button
                  variant="outlined"
                  onClick={handleBackToIndustry}
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    '&:hover': { borderColor: 'white', bgcolor: alpha('#fff', 0.1) },
                    px: 3,
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600
                  }}
                >
                  Back to Industry
                </Button>
              )}

              {/* Status workflow buttons - Only for industry view (not sub-industry) */}
              {!isNewIndustry && viewMode === 'industry' && industry && (
                <>
                  {/* Submit for Review - Only for draft status */}
                  {industry.status === 'draft' && (
                    <Button
                      variant="outlined"
                      onClick={handleSubmitForReview}
                      startIcon={<SendIcon />}
                      sx={{
                        borderColor: 'white',
                        color: 'white',
                        '&:hover': { borderColor: 'white', bgcolor: alpha('#fff', 0.1) },
                        px: 3,
                        py: 1.5,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600
                      }}
                    >
                      Submit for Review
                    </Button>
                  )}

                  {/* Approve - Only for pending_review status and super admins */}
                  {isSuperAdmin && industry.status === 'pending_review' && (
                    <Button
                      variant="outlined"
                      onClick={handleApprove}
                      startIcon={<ApproveIcon />}
                      sx={{
                        borderColor: 'white',
                        color: 'white',
                        '&:hover': { borderColor: 'white', bgcolor: alpha('#fff', 0.1) },
                        px: 3,
                        py: 1.5,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600
                      }}
                    >
                      Approve & Publish
                    </Button>
                  )}

                  {/* Hide - Only for published status and super admins */}
                  {isSuperAdmin && industry.status === 'published' && (
                    <Button
                      variant="outlined"
                      onClick={handleHideIndustry}
                      startIcon={<HideIcon />}
                      sx={{
                        borderColor: 'white',
                        color: 'white',
                        '&:hover': { borderColor: 'white', bgcolor: alpha('#fff', 0.1) },
                        px: 3,
                        py: 1.5,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600
                      }}
                    >
                      Hide Industry
                    </Button>
                  )}
                </>
              )}

              <Button
                variant="outlined"
                onClick={() => window.print()}
                startIcon={<PrintIcon />}
                sx={{
                  bgcolor: 'white',
                  color: theme.palette.primary.main,
                  borderColor: 'white',
                  '&:hover': { bgcolor: alpha('#fff', 0.9), borderColor: 'white' },
                  px: 3,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                Print
              </Button>

              <Button
                variant="contained"
                onClick={handleSave}
                startIcon={<SaveIcon />}
                sx={{
                  bgcolor: 'white',
                  color: theme.palette.primary.main,
                  '&:hover': { bgcolor: alpha('#fff', 0.9) },
                  px: 3,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                {isNewIndustry ? 'Create Industry' : 'Save Changes'}
              </Button>
            </Box>
          </Box>
        </Box>
      </MotionPaper>

      {/* Market Summary Section */}
      {currentData?.summary && (
        <Paper
          elevation={2}
          sx={{
            mb: 6,
            p: { xs: 3, md: 4 },
            borderRadius: 3,
            background: `linear-gradient(to right, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.background.paper, 1)} 70%)`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
          }}
        >
          <Typography
            variant="h4"
            gutterBottom
            sx={{
              fontWeight: 600,
              mb: 2,
              position: 'relative',
              display: 'inline-block',
              '&:after': {
                content: '""',
                position: 'absolute',
                bottom: -8,
                left: 0,
                width: '50%',
                height: 4,
                borderRadius: 2,
                backgroundColor: theme.palette.primary.main
              }
            }}
          >
            Market Summary
          </Typography>

          <EditableText
            value={currentData.summary}
            onSave={(value) => updateCurrentData('summary', value)}
            variant="body1"
            multiline
            rows={6}
            placeholder="Enter market summary..."
            sx={{
              mt: 3,
              lineHeight: 1.8,
              color: theme.palette.text.primary
            }}
          />
        </Paper>
      )}

      {/* Form Mode */}
      <>
          {/* Main Content Tabs */}
          <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              textColor="primary"
              indicatorColor="primary"
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                borderBottom: `1px solid ${theme.palette.divider}`,
                '& .MuiTab-root': {
                  minHeight: 64,
                  px: 3,
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: '1rem'
                }
              }}
            >
          <Tab label="Market Overview" icon={getTabIcon(0)} iconPosition="start" />
          <Tab label="Legal & Infrastructure" icon={getTabIcon(1)} iconPosition="start" />
          <Tab label="Opportunities & Incentives" icon={getTabIcon(2)} iconPosition="start" />
          <Tab label="Trends" icon={getTabIcon(3)} iconPosition="start" />
          <Tab label="Technology" icon={getTabIcon(4)} iconPosition="start" />
          <Tab label="Value Chain" icon={getTabIcon(5)} iconPosition="start" />
          <Tab label="Support Ecosystem" icon={getTabIcon(6)} iconPosition="start" />
          {viewMode === 'industry' && (
            <Tab label="Sub-Industries" />
          )}
          {viewMode === 'industry' && (
            <Tab label="Basic Information" />
          )}
        </Tabs>

        <Box sx={{ p: 4 }}>
          {/* Market Overview Tab */}
          {tabValue === 0 && (
            <Box>
              <RenderMarketOverview industry={currentData || industry} onChange={(field, value) => updateCurrentData(field, value)} />
            </Box>
          )}

          {/* Legal & Infrastructure Tab */}
          {tabValue === 1 && (
            <Box>
              <RenderLegalInfrastructure industry={currentData || industry} onChange={(field, value) => updateCurrentData(field, value)} />
            </Box>
          )}

          {/* Opportunities & Incentives Tab */}
          {tabValue === 2 && (
            <Box>
              <RenderIncentives industry={currentData || industry} onChange={(field, value) => updateCurrentData(field, value)} />
            </Box>
          )}

          {/* Trends Tab */}
          {tabValue === 3 && (
            <Box>
              <RenderTrends industry={currentData || industry} onChange={(field, value) => updateCurrentData(field, value)} />
            </Box>
          )}

          {/* Technology Tab */}
          {tabValue === 4 && (
            <Box>
              <RenderTechnology industry={currentData || industry} onChange={(field, value) => updateCurrentData(field, value)} />
            </Box>
          )}

          {/* Value Chain Tab */}
          {tabValue === 5 && (
            <Box>
              <Typography variant="h5" gutterBottom fontWeight={600}>
                Value Chain
              </Typography>

              <Paper
                elevation={1}
                sx={{
                  mt: 2,
                  borderRadius: 3,
                  overflow: 'hidden'
                }}
              >
                <Box
                  sx={{
                    p: 3,
                    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                    color: 'white'
                  }}
                >
                  <Typography variant="h6" fontWeight={600}>
                    Industry Value Chain Analysis
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                    Detailed breakdown of the value chain stages and components
                  </Typography>
                </Box>

                <Box p={3}>
                  {((viewMode === 'industry' && industry.subIndustries && industry.subIndustries.length > 0) ||
                    (viewMode === 'subindustry' && selectedSubIndustry?.subIndustries && selectedSubIndustry.subIndustries.length > 0) ||
                    (viewMode === 'subindustry' && selectedSubIndustry?.valueChainStages && selectedSubIndustry.valueChainStages.length > 0) ||
                    (viewMode === 'industry' && industry?.valueChainStages && industry.valueChainStages.length > 0) ||
                    currentData?.valueChain) ? (
                    <EditableValueChainTable
                      valueChainId={viewMode === 'subindustry' && selectedSubIndustry ? selectedSubIndustry.id : industry.id}
                      valueChainData={(() => {
                        // Check if there's saved value chain data (from manual edits) - THIS TAKES PRIORITY
                        if (currentData?.valueChain && currentData.valueChain.stages && currentData.valueChain.stages.length > 0) {
                          return currentData.valueChain;
                        }

                        // Get sub-industries - use them to create initial structure
                        const subIndsList = viewMode === 'industry' && industry.subIndustries && industry.subIndustries.length > 0
                          ? industry.subIndustries.filter((sub: SubIndustry) => !sub.parentSubIndustryId)
                          : viewMode === 'subindustry' && selectedSubIndustry?.subIndustries
                          ? selectedSubIndustry.subIndustries.filter((sub: SubIndustry) => sub.parentSubIndustryId === selectedSubIndustry.id)
                          : [];

                        // Create numbered stages (01, 02, 03, 04) with sub-industries as content
                        if (subIndsList.length > 0) {
                          const stages = subIndsList.map((sub: SubIndustry, index: number) => ({
                            name: String(index + 1).padStart(2, '0'),
                            content: sub.name,
                            categories: {},
                            order: sub.displayOrder || index
                          }));

                          return { stages, additionalContent: [] };
                        }

                        // Check if we have value chain stages from the database
                        if (viewMode === 'subindustry' && selectedSubIndustry?.valueChainStages && selectedSubIndustry.valueChainStages.length > 0) {
                          return {
                            stages: selectedSubIndustry.valueChainStages.sort((a: any, b: any) => (a.order || 0) - (b.order || 0)),
                            additionalContent: []
                          };
                        }

                        if (viewMode === 'industry' && industry?.valueChainStages && industry.valueChainStages.length > 0) {
                          return {
                            stages: industry.valueChainStages.sort((a: any, b: any) => (a.order || 0) - (b.order || 0)),
                            additionalContent: []
                          };
                        }

                        // Default empty structure
                        return {
                          stages: [],
                          additionalContent: []
                        };
                      })()}
                      industryId={industry.id}
                      subIndustryId={viewMode === 'subindustry' && selectedSubIndustry ? selectedSubIndustry.id : undefined}
                      onNavigate={(stageName: string, stageId?: string) => {
                        // Navigate to sub-industry edit page when clicking on value chain item
                        if (viewMode === 'industry' && stageId) {
                          // For industry view, navigate to sub-industry edit page
                          navigate(`/admin/sub-industry-details/${industry.id}/${stageId}`);
                        } else if (viewMode === 'subindustry' && stageId && selectedSubIndustry) {
                          // For sub-industry view, navigate to sub-sub-industry edit page
                          navigate(`/admin/sub-industry-details/${industry.id}/${stageId}`);
                        }
                      }}
                      onUpdate={(updatedValueChain: any) => {
                        // Update the value chain in the current data
                        updateCurrentData('valueChain', updatedValueChain);
                      }}
                    />
                  ) : (
                    <Paper
                      elevation={1}
                      sx={{
                        p: 4,
                        borderRadius: 2,
                        background: alpha(theme.palette.warning.light, 0.1),
                        border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`
                      }}
                    >
                      <Typography variant="h6" color="warning.dark" fontWeight={600} gutterBottom>
                        Value Chain Data Not Available
                      </Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                        Value chain data for this {viewMode === 'subindustry' ? 'sub-industry' : 'industry'} is currently being developed.
                      </Typography>
                      <Button
                        variant="outlined"
                        color="warning"
                        startIcon={<AddIcon />}
                        onClick={() => {
                          const initialValueChain = {
                            stages: [
                              {
                                name: 'Import',
                                content: '',
                                categories: {}
                              },
                              {
                                name: 'Research & Discovery',
                                content: '',
                                categories: {}
                              },
                              {
                                name: 'Development',
                                content: '',
                                categories: {}
                              },
                              {
                                name: 'Manufacturing',
                                content: '',
                                categories: {}
                              },
                              {
                                name: 'Commercialization',
                                content: '',
                                categories: {}
                              }
                            ],
                            additionalContent: []
                          };
                          updateCurrentData('valueChain', initialValueChain);
                        }}
                        sx={{ borderRadius: 2, textTransform: 'none' }}
                      >
                        Add Value Chain Data
                      </Button>
                    </Paper>
                  )}
                </Box>
              </Paper>
            </Box>
          )}

          {/* Support Ecosystem Tab */}
          {tabValue === 6 && (
            <Box>
              <Typography variant="h5" gutterBottom fontWeight={600} sx={{ mb: 3 }}>
                Support Ecosystem
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Manage support ecosystem items across 4 categories. Each category has specific subsections that match the user-facing detail pages.
              </Typography>

              <UserFriendlySupportEcosystemEditor
                industryId={currentData?.id || ''}
                subIndustryId={viewMode === 'subindustry' ? selectedSubIndustry?.id : undefined}
                onUpdate={() => {
                  // Reload industry data to reflect changes
                  if (industry?.id) {
                    industryApi.getIndustryById(industry.id).then(setIndustry);
                  }
                }}
              />
            </Box>
          )}

          {/* Sub-Industries Tab (only for industry view) */}
          {tabValue === 7 && viewMode === 'industry' && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                  <Typography variant="h5" fontWeight={600} gutterBottom color="primary">
                    Sub-Industries
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {industry.subIndustries?.length || 0} sub-industries configured
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddSubIndustry}
                  sx={{ borderRadius: 2 }}
                >
                  Add Sub-Industry
                </Button>
              </Box>

              {industry.subIndustries && industry.subIndustries.length > 0 ? (
                <Grid container spacing={3}>
                  {industry.subIndustries.map((subIndustry, index) => (
                    <Grid item xs={12} sm={6} md={4} key={subIndustry.id}>
                      <Card
                        elevation={2}
                        sx={{
                          borderRadius: 2,
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-8px)',
                            boxShadow: '0 12px 20px -10px rgba(0,0,0,0.2)'
                          },
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column'
                        }}
                      >
                        <CardActionArea
                          onClick={() => handleSubIndustryClick(subIndustry)}
                          sx={{
                            flexGrow: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            height: '100%'
                          }}
                        >
                          <Box
                            sx={{
                              height: 8,
                              width: '100%',
                              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light} 50%, transparent)`
                            }}
                          />

                          <CardContent sx={{ p: 3, flexGrow: 1, width: '100%' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                              <Typography
                                variant="h6"
                                sx={{
                                  fontWeight: 600,
                                  color: theme.palette.text.primary
                                }}
                              >
                                {subIndustry.name}
                              </Typography>
                              <Chip
                                label={subIndustry.isActive !== false ? 'Active' : 'Inactive'}
                                color={subIndustry.isActive !== false ? 'success' : 'default'}
                                size="small"
                              />
                            </Box>

                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mb: 2 }}
                            >
                              {subIndustry.description}
                            </Typography>

                            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditSubIndustry(subIndustry);
                                }}
                                startIcon={<EditIcon />}
                              >
                                Edit
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteSubIndustry(subIndustry.id);
                                }}
                                startIcon={<DeleteIcon />}
                              >
                                Delete
                              </Button>
                            </Box>

                            <Box sx={{ mt: 'auto', display: 'flex', alignItems: 'center', color: theme.palette.primary.main }}>
                              <Typography variant="button" sx={{ mr: 0.5 }}>
                                View Details
                              </Typography>
                              <ArrowForwardIcon fontSize="small" />
                            </Box>
                          </CardContent>
                        </CardActionArea>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No Sub-Industries
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    This industry doesn't have any sub-industries configured yet.
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddSubIndustry}
                    sx={{ borderRadius: 2 }}
                  >
                    Add First Sub-Industry
                  </Button>
                </Paper>
              )}
            </Box>
          )}

          {/* Basic Information Tab (only for industry view) */}
          {tabValue === 8 && viewMode === 'industry' && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h5" fontWeight={600} gutterBottom color="primary">
                  Basic Information
                </Typography>
                <Divider sx={{ mb: 3 }} />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Industry Name
                </Typography>
                <EditableText
                  value={industry.name}
                  onSave={(value) => handleInputChange('name', value)}
                  placeholder="Enter industry name..."
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={industry.category}
                    label="Category"
                    onChange={(e) => handleInputChange('category', e.target.value)}
                    sx={{ borderRadius: 2 }}
                  >
                    {categories.map((category) => (
                      <MenuItem key={category} value={category}>
                        {category}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Icon
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => setIconPickerOpen(true)}
                  sx={{
                    width: '100%',
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                    borderRadius: 2,
                    p: 1.5
                  }}
                  startIcon={getIndustryIcon(industry.icon)}
                >
                  {industry.icon || 'Select Icon...'}
                </Button>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Description
                </Typography>
                <EditableText
                  value={industry.description || ''}
                  onSave={(value) => handleInputChange('description', value)}
                  multiline
                  rows={4}
                  placeholder="Enter industry description..."
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Short Description
                </Typography>
                <EditableText
                  value={industry.shortDescription || ''}
                  onSave={(value) => handleInputChange('shortDescription', value)}
                  multiline
                  rows={2}
                  placeholder="Enter brief description for cards and previews..."
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Tags (comma-separated)
                </Typography>
                <EditableText
                  value={industry.tags ? industry.tags.join(', ') : ''}
                  onSave={handleTagsChange}
                  placeholder="Enter tags separated by commas..."
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Market Summary
                </Typography>
                <EditableText
                  value={industry.summary || ''}
                  onSave={(value) => handleInputChange('summary', value)}
                  multiline
                  rows={6}
                  placeholder="Enter comprehensive market summary that provides an overview of the industry landscape, key trends, and market dynamics..."
                />
              </Grid>

              {/* Market Data Section */}
              <Grid item xs={12}>
                <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mt: 3 }}>
                  Market Data & Metrics
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Growth Rate (%)"
                  type="number"
                  value={industry.growthRate}
                  onChange={(e) => handleInputChange('growthRate', parseFloat(e.target.value) || 0)}
                  InputProps={{ inputProps: { min: 0, max: 100, step: 0.1 } }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Market Size
                </Typography>
                <EditableText
                  value={industry.marketSize || ''}
                  onSave={(value) => handleInputChange('marketSize', value)}
                  placeholder="e.g., $2.8B, SAR 10.5B"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Investment Opportunities"
                  type="number"
                  value={industry.investmentOpportunities}
                  onChange={(e) => handleInputChange('investmentOpportunities', parseInt(e.target.value) || 0)}
                  InputProps={{ inputProps: { min: 0 } }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>

              {/* Opportunities & Trends Section */}
              <Grid item xs={12}>
                <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mt: 3 }}>
                  Opportunities & Trends
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              {/* Opportunities */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom color="primary">
                  Opportunities (3 bullet points)
                </Typography>
                {[0, 1, 2].map((index) => (
                  <TextField
                    key={`opportunity-${index}`}
                    fullWidth
                    multiline
                    rows={2}
                    label={`Opportunity ${index + 1}`}
                    value={industry.opportunities?.[index] || ''}
                    onChange={(e) => {
                      const newOpportunities = [...(industry.opportunities || ['', '', ''])];
                      newOpportunities[index] = e.target.value;
                      handleInputChange('opportunities', newOpportunities);
                    }}
                    placeholder={`Enter opportunity ${index + 1}...`}
                    sx={{
                      mb: 2,
                      '& .MuiOutlinedInput-root': { borderRadius: 2 }
                    }}
                  />
                ))}
              </Grid>

              {/* Trends */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom color="secondary">
                  Trends (3 bullet points)
                </Typography>
                {[0, 1, 2].map((index) => (
                  <TextField
                    key={`trend-${index}`}
                    fullWidth
                    multiline
                    rows={2}
                    label={`Trend ${index + 1}`}
                    value={industry.trends?.[index] || ''}
                    onChange={(e) => {
                      const newTrends = [...(industry.trends || ['', '', ''])];
                      newTrends[index] = e.target.value;
                      handleInputChange('trends', newTrends);
                    }}
                    placeholder={`Enter trend ${index + 1}...`}
                    sx={{
                      mb: 2,
                      '& .MuiOutlinedInput-root': { borderRadius: 2 }
                    }}
                  />
                ))}
              </Grid>

              {/* Key Metrics Section */}
              <Grid item xs={12}>
                <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mt: 2 }}>
                  Key Metrics
                </Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Employment
                </Typography>
                <EditableText
                  value={industry.keyMetrics?.employment || ''}
                  onSave={(value) => handleKeyMetricsChange('employment', value)}
                  placeholder="e.g., 25,000+, 50K workers"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Exports
                </Typography>
                <EditableText
                  value={industry.keyMetrics?.exports || ''}
                  onSave={(value) => handleKeyMetricsChange('exports', value)}
                  placeholder="e.g., $1.2B, SAR 4.5B"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Number of Companies"
                  type="number"
                  value={industry.keyMetrics?.companies || 0}
                  onChange={(e) => handleKeyMetricsChange('companies', parseInt(e.target.value) || 0)}
                  InputProps={{ inputProps: { min: 0 } }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>

              {/* Advanced Settings */}
              <Grid item xs={12}>
                <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mt: 3 }}>
                  Advanced Settings
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={industry.isActive !== false}
                      onChange={(e) => handleInputChange('isActive', e.target.checked)}
                    />
                  }
                  label="Active Industry"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={industry.hasSubIndustries}
                      onChange={(e) => handleInputChange('hasSubIndustries', e.target.checked)}
                    />
                  }
                  label="Has Sub-Industries"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Priority (1-10)"
                  type="number"
                  value={industry.priority || 1}
                  onChange={(e) => handleInputChange('priority', parseInt(e.target.value) || 1)}
                  InputProps={{ inputProps: { min: 1, max: 10 } }}
                  helperText="Display priority (1 = highest)"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Last Updated"
                  value={industry.lastUpdated ? new Date(industry.lastUpdated).toLocaleString() : 'Never'}
                  disabled
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>
          )}
        </Box>
      </Paper>
      </>

      {/* Sub-Industry Dialog */}
      <Dialog
        open={subIndustryDialog}
        onClose={() => setSubIndustryDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight={600}>
              {editingSubIndustry?.id ? 'Edit Sub-Industry' : 'Add New Sub-Industry'}
            </Typography>
            <IconButton onClick={() => setSubIndustryDialog(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        {editingSubIndustry && (
          <DialogContent sx={{ p: 3 }}>
            <Grid container spacing={3} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Name"
                  value={editingSubIndustry.name}
                  onChange={(e) => setEditingSubIndustry(prev => prev ? { ...prev, name: e.target.value } : null)}
                  required
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={4}
                  value={editingSubIndustry.description}
                  onChange={(e) => setEditingSubIndustry(prev => prev ? { ...prev, description: e.target.value } : null)}
                  required
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Summary"
                  multiline
                  rows={4}
                  value={editingSubIndustry.summary}
                  onChange={(e) => setEditingSubIndustry(prev => prev ? { ...prev, summary: e.target.value } : null)}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Market Size"
                  value={editingSubIndustry.marketSize || ''}
                  onChange={(e) => setEditingSubIndustry(prev => prev ? { ...prev, marketSize: e.target.value } : null)}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Growth Rate (%)"
                  type="number"
                  value={editingSubIndustry.growthRate || 0}
                  onChange={(e) => setEditingSubIndustry(prev => prev ? { ...prev, growthRate: parseFloat(e.target.value) || 0 } : null)}
                  InputProps={{ inputProps: { min: 0, max: 100, step: 0.1 } }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editingSubIndustry.isActive !== false}
                      onChange={(e) => setEditingSubIndustry(prev => prev ? { ...prev, isActive: e.target.checked } : null)}
                    />
                  }
                  label="Active Sub-Industry"
                />
              </Grid>
            </Grid>
          </DialogContent>
        )}
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setSubIndustryDialog(false)} variant="outlined" sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button onClick={handleSaveSubIndustry} variant="contained" startIcon={<SaveIcon />} sx={{ borderRadius: 2 }}>
            Save Sub-Industry
          </Button>
        </DialogActions>
      </Dialog>

      {/* Floating Save Button */}
      <Fab
        color="primary"
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 1000
        }}
        onClick={handleSave}
      >
        <SaveIcon />
      </Fab>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Icon Picker Dialog */}
      <IconPicker
        open={iconPickerOpen}
        selectedIcon={industry?.icon || ''}
        onClose={() => setIconPickerOpen(false)}
        onSelect={(iconName) => handleInputChange('icon', iconName)}
      />

      {/* Print Header - Only visible when printing */}
      <Box className="print-header">
        <Typography variant="h3" component="h1" sx={{ fontWeight: 'bold', mb: 1 }}>
          VALUE CHAIN GLOBAL
        </Typography>
        <Typography variant="subtitle1">
          Industry Intelligence & Analysis Platform • Saudi Vision 2030
        </Typography>
        <Typography variant="h5" sx={{ mt: 2 }}>
          {currentData?.name || 'Industry Report'}
        </Typography>
      </Box>

      {/* Print Footer - Only visible when printing */}
      <Box className="print-footer">
        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
          VALUE CHAIN GLOBAL
        </Typography>
        <Typography variant="caption">
          www.value-chain.global • Connecting Industries • Empowering Growth
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
          © {new Date().getFullYear()} Vision 2030 Initiative
        </Typography>
      </Box>
    </Container>
    </>
  );
};

export default IndustryDetailsPage;